import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Button, Tag, Empty, List, Badge } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import { educationSessionService } from '@iso/services';

/**
 * The attendee list for a session, refreshing itself while it is on screen.
 *
 * Shown next to a QR code in two places — the wizard, straight after a session
 * is activated, and the reopenable QR modal — because in both the trainer is
 * watching for the same thing: names appearing as people scan. A list that only
 * reflected the moment it was opened would answer the wrong question.
 *
 * How often it re-reads: this is left up on a projector for the length of a
 * session, so the interval is a standing cost rather than a one-off. 8s is fast
 * enough that somebody who has just scanned sees their name while they are
 * still looking at the screen, and slow enough that an hour-long induction
 * costs ~450 requests rather than several thousand.
 */
const POLL_MS = 8000;

const LiveAttendeeRoster = ({ sessionId, enabled = true, maxHeight = 320 }) => {
  const [attendees, setAttendees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  // A ref, not state: the poller reads it to decide whether to skip a tick, and
  // holding it in state would re-create the interval on every fetch.
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (!sessionId || inFlight.current) return;
    inFlight.current = true;
    try {
      const res = await educationSessionService.getTrainees(sessionId);
      if (res && res.trainees) {
        setAttendees(res.trainees);
        setLastSynced(moment());
      }
    } catch (e) {
      // Deliberately silent. A dropped poll is recovered by the next tick, and
      // a toast every 8s on a flaky connection would bury the QR code the
      // trainer is actually displaying.
    } finally {
      inFlight.current = false;
    }
  }, [sessionId]);

  useEffect(() => {
    if (!enabled || !sessionId) {
      setAttendees([]);
      setLastSynced(null);
      return undefined;
    }

    setLoading(true);
    load().finally(() => setLoading(false));

    const timer = setInterval(() => {
      // Don't poll into a backgrounded tab. A trainer who switches away still
      // has the code up on the projector; catching up on return is enough, and
      // it stops a forgotten tab polling all night.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      load();
    }, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled, sessionId, load]);

  return (
    <div style={{ textAlign: 'left' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <Badge status={loading ? 'processing' : 'success'} />
        <span style={{ fontWeight: 600 }}>Registered attendees ({attendees.length})</span>
        <div style={{ flex: 1 }} />
        <Button type="text" size="small" icon={<ReloadOutlined />} onClick={load} title="Refresh now" />
      </div>

      <div style={{ fontSize: 11, color: '#8c8c8c', marginBottom: 10 }}>
        {lastSynced
          ? `Updates automatically · last checked ${lastSynced.format('HH:mm:ss')}`
          : 'Updates automatically as people scan the code.'}
      </div>

      {attendees.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nobody has scanned yet"
          style={{ margin: '24px 0' }}
        />
      ) : (
        <List
          size="small"
          bordered
          style={{ maxHeight, overflowY: 'auto', borderRadius: 8 }}
          dataSource={attendees}
          rowKey={(a) => a.id}
          renderItem={(a) => (
            <List.Item>
              <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {a.full_name || a.user_name || '—'}
                  </div>
                  {a.email && (
                    <div style={{ fontSize: 11, color: '#8c8c8c', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {a.email}
                    </div>
                  )}
                </div>
                <Tag style={{ marginRight: 0 }}>
                  {a.trainee_type === 'property_user' ? 'Staff' : 'External'}
                </Tag>
              </div>
            </List.Item>
          )}
        />
      )}
    </div>
  );
};

export default LiveAttendeeRoster;
